import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Loader2, Send, FileText, Users, Upload, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { WhatsAppTemplate } from '@/types/whatsapp';

interface Contact {
  phone: string;
  name?: string;
}

export function WhatsAppSendMessage() {
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [activeTab, setActiveTab] = useState('individual');
  
  const [individualMessage, setIndividualMessage] = useState({
    phone: '',
    message: '',
    variables: {} as Record<string, string>
  });

  const [bulkMessage, setBulkMessage] = useState({
    message: '',
    contacts: [] as Contact[],
    csvText: ''
  });

  const [sendResults, setSendResults] = useState<{
    success: boolean;
    message: string;
    details?: any;
  } | null>(null);

  const { toast } = useToast();

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const response = await fetch('/api/admin/whatsapp/templates');
      const data = await response.json();
      
      if (data.success) {
        setTemplates(data.data);
      }
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  };

  const formatPhoneNumber = (value: string) => {
    // Remove all non-digit characters
    const digits = value.replace(/\D/g, '');
    
    // Format as (DD) XXXXX-XXXX or (DD) XXXX-XXXX
    if (digits.length <= 2) {
      return digits;
    } else if (digits.length <= 7) {
      return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    } else if (digits.length <= 11) {
      return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
    } else {
      return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7, 11)}`;
    }
  };

  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplate(templateId);
    const template = templates.find(t => t.id === templateId);

    if (template && templateId !== 'no-template') {
      if (activeTab === 'individual') {
        setIndividualMessage(prev => ({
          ...prev,
          message: template.message,
          variables: template.variables?.reduce((acc, variable) => ({
            ...acc,
            [variable]: ''
          }), {}) || {}
        }));
      } else {
        setBulkMessage(prev => ({
          ...prev,
          message: template.message
        }));
      }
    }
  };

  const handleVariableChange = (variable: string, value: string) => {
    setIndividualMessage(prev => ({
      ...prev,
      variables: {
        ...prev.variables,
        [variable]: value
      }
    }));
  };

  const formatMessageWithVariables = (message: string, variables: Record<string, string>) => {
    let formatted = message;
    Object.entries(variables).forEach(([key, value]) => {
      const placeholder = `{{${key}}}`;
      formatted = formatted.replace(new RegExp(placeholder, 'g'), value);
    });
    return formatted;
  };

  const sendIndividualMessage = async () => {
    if (!individualMessage.phone || !individualMessage.message) {
      toast({
        title: "Erro",
        description: "Preencha todos os campos obrigatórios.",
        variant: "destructive"
      });
      return;
    }

    setIsSending(true);
    setSendResults(null);

    try {
      const formattedMessage = formatMessageWithVariables(
        individualMessage.message,
        individualMessage.variables
      );

      const response = await fetch('/api/admin/whatsapp/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone: individualMessage.phone,
          message: formattedMessage,
          template_id: selectedTemplate
        })
      });

      const data = await response.json();
      
      setSendResults({
        success: data.success,
        message: data.message,
        details: data.data
      });

      toast({
        title: data.success ? "Sucesso" : "Erro",
        description: data.message,
        variant: data.success ? "default" : "destructive"
      });

      if (data.success) {
        // Reset form
        setIndividualMessage({
          phone: '',
          message: '',
          variables: {}
        });
        setSelectedTemplate('');
      }
    } catch (error) {
      const message = "Erro ao enviar mensagem";
      setSendResults({
        success: false,
        message
      });
      toast({
        title: "Erro",
        description: message,
        variant: "destructive"
      });
    } finally {
      setIsSending(false);
    }
  };

  const handleCSVUpload = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const csvText = event.target.value;
    setBulkMessage(prev => ({ ...prev, csvText }));

    if (!csvText.trim()) {
      setBulkMessage(prev => ({ ...prev, contacts: [] }));
      return;
    }

    try {
      const lines = csvText.split('\n').filter(line => line.trim());
      const contacts: Contact[] = [];

      lines.forEach((line, index) => {
        const values = line.split(',').map(v => v.trim());
        if (values.length >= 1) {
          const phone = values[0].replace(/\D/g, '');
          if (phone.length >= 10 && phone.length <= 11) {
            contacts.push({
              phone,
              name: values[1] || `Contato ${index + 1}`
            });
          }
        }
      });

      setBulkMessage(prev => ({ ...prev, contacts }));
    } catch (error) {
      console.error('Error parsing CSV:', error);
      toast({
        title: "Erro",
        description: "Formato do CSV inválido. Use: telefone,nome (um por linha)",
        variant: "destructive"
      });
    }
  };

  const sendBulkMessages = async () => {
    if (bulkMessage.contacts.length === 0 || !bulkMessage.message) {
      toast({
        title: "Erro",
        description: "Adicione contatos e uma mensagem.",
        variant: "destructive"
      });
      return;
    }

    setIsSending(true);
    setSendResults(null);

    try {
      const promises = bulkMessage.contacts.map(contact => {
        const personalizedMessage = contact.name 
          ? bulkMessage.message.replace(/\{\{nome\}\}/g, contact.name)
          : bulkMessage.message;

        return fetch('/api/admin/whatsapp/send', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            phone: contact.phone,
            message: personalizedMessage,
            template_id: selectedTemplate
          })
        });
      });

      const results = await Promise.allSettled(promises);
      
      const successful = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;

      const message = `Envio concluído: ${successful} enviadas com sucesso, ${failed} falharam.`;
      const success = failed === 0;

      setSendResults({
        success,
        message,
        details: { successful, failed, total: bulkMessage.contacts.length }
      });

      toast({
        title: success ? "Sucesso" : "Concluído com erros",
        description: message,
        variant: success ? "default" : "destructive"
      });

      if (success) {
        // Reset form
        setBulkMessage({
          message: '',
          contacts: [],
          csvText: ''
        });
        setSelectedTemplate('');
      }
    } catch (error) {
      const message = "Erro ao enviar mensagens em lote";
      setSendResults({
        success: false,
        message
      });
      toast({
        title: "Erro",
        description: message,
        variant: "destructive"
      });
    } finally {
      setIsSending(false);
    }
  };

  const selectedTemplateData = templates.find(t => t.id === selectedTemplate);
  const hasVariables = selectedTemplateData && selectedTemplateData.variables && selectedTemplateData.variables.length > 0;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Send className="h-5 w-5" />
            <span>Enviar Mensagens WhatsApp</span>
          </CardTitle>
          <CardDescription>
            Envie mensagens individuais ou em lote para seus contatos
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList>
              <TabsTrigger value="individual" className="flex items-center space-x-2">
                <Users className="h-4 w-4" />
                <span>Mensagem Individual</span>
              </TabsTrigger>
              <TabsTrigger value="bulk" className="flex items-center space-x-2">
                <Upload className="h-4 w-4" />
                <span>Envio em Lote</span>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="individual" className="space-y-4">
              {/* Template Selection */}
              <div className="space-y-2">
                <Label>Template (Opcional)</Label>
                <Select value={selectedTemplate} onValueChange={handleTemplateSelect}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione um template..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="no-template">Sem template</SelectItem>
                    {templates.map(template => (
                      <SelectItem key={template.id} value={template.id}>
                        {template.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phone">Telefone *</Label>
                  <Input
                    id="phone"
                    value={individualMessage.phone}
                    onChange={(e) => {
                      const formatted = formatPhoneNumber(e.target.value);
                      setIndividualMessage(prev => ({ ...prev, phone: formatted }));
                    }}
                    placeholder="(11) 99999-9999"
                  />
                  <p className="text-sm text-muted-foreground">
                    Formato: (DD) XXXXX-XXXX
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="message">Mensagem *</Label>
                <Textarea
                  id="message"
                  value={individualMessage.message}
                  onChange={(e) => setIndividualMessage(prev => ({ ...prev, message: e.target.value }))}
                  placeholder="Digite sua mensagem aqui..."
                  rows={4}
                />
                <div className="flex justify-between text-sm text-muted-foreground">
                  <span>{individualMessage.message.length} caracteres</span>
                  {selectedTemplateData && (
                    <Badge variant="outline">
                      Template: {selectedTemplateData.name}
                    </Badge>
                  )}
                </div>
              </div>

              {/* Variable Inputs */}
              {hasVariables && (
                <div className="space-y-3 p-4 bg-muted rounded-lg">
                  <Label className="text-sm font-medium">Variáveis do Template</Label>
                  {selectedTemplateData.variables!.map(variable => (
                    <div key={variable} className="space-y-1">
                      <Label htmlFor={`var-${variable}`} className="text-sm">
                        {variable.charAt(0).toUpperCase() + variable.slice(1)}
                      </Label>
                      <Input
                        id={`var-${variable}`}
                        value={individualMessage.variables[variable] || ''}
                        onChange={(e) => handleVariableChange(variable, e.target.value)}
                        placeholder={`Valor para {{${variable}}}`}
                      />
                    </div>
                  ))}
                </div>
              )}

              <Button 
                onClick={sendIndividualMessage} 
                disabled={isSending || !individualMessage.phone || !individualMessage.message}
                className="w-full"
              >
                {isSending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Enviar Mensagem
                  </>
                )}
              </Button>
            </TabsContent>

            <TabsContent value="bulk" className="space-y-4">
              {/* Template Selection */}
              <div className="space-y-2">
                <Label>Template (Opcional)</Label>
                <Select value={selectedTemplate} onValueChange={handleTemplateSelect}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione um template..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="no-template">Sem template</SelectItem>
                    {templates.map(template => (
                      <SelectItem key={template.id} value={template.id}>
                        {template.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="csv">Contatos (CSV)</Label>
                <Textarea
                  id="csv"
                  value={bulkMessage.csvText}
                  onChange={handleCSVUpload}
                  placeholder="telefone,nome&#10;11999999999,João Silva&#10;21888888888,Maria Santos&#10;&#10;Um contato por linha"
                  rows={6}
                />
                <p className="text-sm text-muted-foreground">
                  Formato: telefone,nome (um por linha). Ex: 11999999999,João Silva
                </p>
              </div>

              {bulkMessage.contacts.length > 0 && (
                <Alert>
                  <CheckCircle className="h-4 w-4" />
                  <AlertDescription>
                    {bulkMessage.contacts.length} contato(s) encontrado(s) e validado(s)
                  </AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="bulkMessage">Mensagem *</Label>
                <Textarea
                  id="bulkMessage"
                  value={bulkMessage.message}
                  onChange={(e) => setBulkMessage(prev => ({ ...prev, message: e.target.value }))}
                  placeholder="Digite sua mensagem aqui..."
                  rows={4}
                />
                <div className="flex justify-between text-sm text-muted-foreground">
                  <span>{bulkMessage.message.length} caracteres</span>
                  {selectedTemplateData && (
                    <Badge variant="outline">
                      Template: {selectedTemplateData.name}
                    </Badge>
                  )}
                </div>
                {selectedTemplateData?.variables?.includes('nome') && (
                  <p className="text-sm text-blue-600">
                    💡 Use {"{{nome}}"} para personalizar com o nome de cada contato
                  </p>
                )}
              </div>

              <Button 
                onClick={sendBulkMessages} 
                disabled={isSending || bulkMessage.contacts.length === 0 || !bulkMessage.message}
                className="w-full"
              >
                {isSending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Enviar para {bulkMessage.contacts.length} contato(s)
                  </>
                )}
              </Button>
            </TabsContent>
          </Tabs>

          {/* Send Results */}
          {sendResults && (
            <Alert className={sendResults.success ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"}>
              <div className="flex items-start">
                {sendResults.success ? (
                  <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-0.5" />
                ) : (
                  <XCircle className="h-4 w-4 text-red-600 mr-2 mt-0.5" />
                )}
                <div className="flex-1">
                  <AlertDescription className={sendResults.success ? "text-green-800" : "text-red-800"}>
                    {sendResults.message}
                    {sendResults.details && (
                      <div className="mt-2 text-sm">
                        Total: {sendResults.details.total} | 
                        Sucesso: {sendResults.details.successful} | 
                        Falhas: {sendResults.details.failed}
                      </div>
                    )}
                  </AlertDescription>
                </div>
              </div>
            </Alert>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
